import {
    Add,
    ChevronRight,
    WorkspacePremium,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardActionArea,
    Chip,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { EmptyContentComponent } from '../../../components/Content';
import { LoadingContent } from '../../../components/Loading';
import ListPageLayout from '../../../components/ListPageLayout';
import { useFetch } from '../../../hooks/useFecth';
import { useSearchTextInput } from '../../../hooks/useSearchTextInput';
import DialogPlans from './components/DialogPlan';

const statusOptions = [
    { value: '', label: 'Todos' },
    { value: 'true', label: 'Ativos' },
    { value: 'false', label: 'Inativos' },
];

function formatCurrency(value) {
    return Number(value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function PlanItem({ item, viewMode }) {
    if (viewMode === 'cards') {
        return (
            <Card variant="outlined" sx={{ height: '100%' }}>
                <CardActionArea component={Link} to={`#plans|${item.id}`} sx={{ p: 2, height: '100%' }}>
                    <Stack spacing={1.25}>
                        <Avatar src={item.image} variant="rounded" sx={{ bgcolor: 'primary.main' }}>
                            <WorkspacePremium />
                        </Avatar>
                        <Typography fontWeight={700}>{item.name}</Typography>
                        <Chip size="small" color={item.status ? 'success' : 'default'} label={item.status ? 'Ativo' : 'Inativo'} />
                        <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(item.price)}
                        </Typography>
                    </Stack>
                </CardActionArea>
            </Card>
        );
    }

    return (
        <Card variant="outlined">
            <CardActionArea component={Link} to={`#plans|${item.id}`} sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Avatar src={item.image} variant="rounded" sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                        <WorkspacePremium />
                    </Avatar>
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography fontWeight={700}>{item.name}</Typography>
                            <Chip label={`#${item.id}`} size="small" />
                            <Chip size="small" color={item.status ? 'success' : 'default'} label={item.status ? 'Ativo' : 'Inativo'} />
                        </Stack>
                        {item.description && (
                            <Typography variant="body2" color="text.secondary" noWrap>
                                {item.description}
                            </Typography>
                        )}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip size="small" variant="outlined" label={`Mensalidade: ${formatCurrency(item.price)}`} />
                            {Boolean(item.numberLifes) && (
                                <Chip size="small" variant="outlined" color="primary" label={`${item.numberLifes} vidas`} />
                            )}
                        </Stack>
                    </Stack>
                    <ChevronRight color="action" sx={{ display: { xs: 'none', sm: 'block' } }} />
                </Stack>
            </CardActionArea>
        </Card>
    );
}

export default function Plans() {
    const [searchParams, setSearchParams] = useSearchParams();
    const page = parseInt(searchParams.get('page'), 10) || 1;
    const pageSize = parseInt(searchParams.get('per_page'), 10) || 10;
    const { search, input, handleChange, handleReset } = useSearchTextInput(searchParams.get('search') || '');
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');

    const fetchParams = useMemo(
        () => ({
            type: 'plan',
            clubId: null,
            paged: true,
            page,
            per_page: pageSize,
            search: search.trim() || undefined,
            status: statusFilter || undefined,
        }),
        [page, pageSize, search, statusFilter],
    );

    const { data, mutate, isFetching } = useFetch('plans', { params: fetchParams });
    const rows = data?.rows ?? [];
    const hasFilters = Boolean(search.trim() || statusFilter);

    const applyFilters = close => {
        const nextParams = new URLSearchParams();
        if (search.trim()) nextParams.set('search', search.trim());
        if (statusFilter) nextParams.set('status', statusFilter);
        if (pageSize !== 10) nextParams.set('per_page', String(pageSize));
        nextParams.set('page', '1');
        setSearchParams(nextParams);
        close?.();
    };

    const handleClearFilters = () => {
        handleReset();
        setStatusFilter('');
        setSearchParams({});
    };

    const handlePageChange = (_, newPage) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('page', String(newPage));
        setSearchParams(nextParams);
    };

    const handlePageSizeChange = nextSize => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('per_page', String(nextSize));
        nextParams.set('page', '1');
        setSearchParams(nextParams);
    };

    return (
        <>
            <ListPageLayout
                title="Planos"
                subtitle="Planos contratados pelo gestor do clube."
                counts={[{ label: 'planos', value: data?.numRows ?? 0, color: 'primary' }]}
                loading={isFetching && !data}
                filterActive={hasFilters}
                onClearFilters={hasFilters ? handleClearFilters : null}
                viewStorageKey="admin-plans-view"
                actions={
                    <Button size="small" variant="contained" startIcon={<Add />} component={Link} to="#plans">
                        Adicionar plano
                    </Button>
                }
                isEmpty={!isFetching && rows.length === 0}
                empty={
                    <Stack spacing={2} alignItems="center">
                        <EmptyContentComponent />
                        <Typography color="text.secondary">
                            {hasFilters ? 'Nenhum plano com esses filtros.' : 'Nenhum plano cadastrado.'}
                        </Typography>
                    </Stack>
                }
                pagination={{
                    page,
                    pages: data?.pages || 1,
                    total: data?.numRows ?? 0,
                    pageSize,
                    onChange: handlePageChange,
                    onPageSizeChange: handlePageSizeChange,
                    itemLabel: (data?.numRows ?? 0) === 1 ? 'plano' : 'planos',
                }}
                filterPanel={({ close }) => (
                    <Stack spacing={2} component="form" onSubmit={e => { e.preventDefault(); applyFilters(close); }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Buscar"
                            value={input}
                            onChange={e => handleChange(e.target.value)}
                        />
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Status"
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value)}
                        >
                            {statusOptions.map(option => (
                                <MenuItem key={option.value || 'all'} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Button type="submit" variant="contained" fullWidth>
                            Aplicar filtros
                        </Button>
                    </Stack>
                )}
            >
                {({ viewMode }) =>
                    isFetching && !data ? (
                        <LoadingContent />
                    ) : (
                        <Box
                            sx={{
                                p: 2,
                                display: viewMode === 'cards' ? 'grid' : 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                gridTemplateColumns:
                                    viewMode === 'cards'
                                        ? { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }
                                        : undefined,
                            }}
                        >
                            {rows.map(item => (
                                <PlanItem key={item.id} item={item} viewMode={viewMode} />
                            ))}
                        </Box>
                    )
                }
            </ListPageLayout>
            <DialogPlans mutate={mutate} />
        </>
    );
}
